/**
 * Browser Notifications Hook
 *
 * Manages browser notification permissions and displays notifications for new chat messages.
 *
 * @module @municipality/chat/useBrowserNotifications
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  playNotificationSound,
  unlockAutoplay,
  supportsAudio,
} from './notificationSound';
import type { Message, Conversation, ParticipantInfo } from '@municipality/types';

type NotificationPermission = globalThis.NotificationPermission;
type NotificationOptions = globalThis.NotificationOptions;

const NOTIFICATIONS_ENABLED_KEY = 'browser_notifications_enabled';
const SOUND_ENABLED_KEY = 'notification_sound_enabled';
const SOUND_VOLUME_KEY = 'notification_sound_volume';

export interface NotificationSettings {
  enabled: boolean;
  soundEnabled: boolean;
  soundVolume: number;
}

const getNotificationSettings = (): NotificationSettings => {
  if (typeof window === 'undefined') {
    return { enabled: false, soundEnabled: true, soundVolume: 0.5 };
  }
  try {
    return {
      enabled: localStorage.getItem(NOTIFICATIONS_ENABLED_KEY) !== 'false',
      soundEnabled: localStorage.getItem(SOUND_ENABLED_KEY) !== 'false',
      soundVolume: parseFloat(localStorage.getItem(SOUND_VOLUME_KEY) ?? '0.5'),
    };
  } catch {
    return { enabled: false, soundEnabled: true, soundVolume: 0.5 };
  }
};

const saveNotificationSettings = (settings: Partial<NotificationSettings>): void => {
  if (typeof window === 'undefined') return;
  try {
    if (settings.enabled !== undefined) {
      localStorage.setItem(NOTIFICATIONS_ENABLED_KEY, String(settings.enabled));
    }
    if (settings.soundEnabled !== undefined) {
      localStorage.setItem(SOUND_ENABLED_KEY, String(settings.soundEnabled));
    }
    if (settings.soundVolume !== undefined) {
      localStorage.setItem(SOUND_VOLUME_KEY, String(settings.soundVolume));
    }
  } catch {
    /**/
  }
};

const supportsNotifications = (): boolean => {
  if (typeof window === 'undefined') return false;
  return 'Notification' in window;
};

export interface UseBrowserNotificationsOptions {
  /** Path to navigate to when user clicks notification (default: '/chat') */
  chatPath?: string;
}

export const useBrowserNotifications = (
  currentUserId: string | undefined,
  options: UseBrowserNotificationsOptions = {}
) => {
  const { chatPath = '/chat' } = options;
  const navigate = useNavigate();
  const [permission, setPermission] = useState<NotificationPermission>(
    supportsNotifications() ? globalThis.Notification.permission : 'denied'
  );
  const [settings, setSettings] = useState<NotificationSettings>(getNotificationSettings());
  const previousMessagesRef = useRef<Map<string, Set<string>>>(new Map());
  const audioUnlockedRef = useRef(false);

  const persistNotificationTracking = useCallback(() => {
    if (typeof window === 'undefined') return;
    try {
      const serialized: Record<string, string[]> = {};
      previousMessagesRef.current.forEach((messageIds, conversationId) => {
        serialized[conversationId] = Array.from(messageIds);
      });
      sessionStorage.setItem('notification_tracking', JSON.stringify(serialized));
    } catch {
      /**/
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const stored = sessionStorage.getItem('notification_tracking');
      if (stored) {
        const parsed = JSON.parse(stored) as Record<string, string[]>;
        const map = new Map<string, Set<string>>();
        Object.entries(parsed).forEach(([conversationId, messageIds]) => {
          map.set(conversationId, new Set(messageIds));
        });
        previousMessagesRef.current = map;
      }
    } catch {
      /**/
    }
  }, []);

  useEffect(() => {
    if (audioUnlockedRef.current) return;
    let hasUnlocked = false;
    const unlockAudio = async () => {
      if (hasUnlocked) return;
      hasUnlocked = true;
      try {
        await unlockAutoplay();
        audioUnlockedRef.current = true;
      } catch {
        try {
          localStorage.removeItem('notification_autoplay_unlocked');
        } catch {
          /**/
        }
      }
    };
    const events = ['click', 'touchstart', 'keydown'];
    const handlers: Array<() => void> = [];
    const createHandler = () => {
      let called = false;
      return () => {
        if (called) return;
        called = true;
        unlockAudio();
        events.forEach((event, index) => {
          document.removeEventListener(event, handlers[index]);
        });
      };
    };
    events.forEach((event) => {
      const handler = createHandler();
      handlers.push(handler);
      document.addEventListener(event, handler, { once: true, passive: true });
    });
    return () => {
      events.forEach((event, index) => {
        if (handlers[index]) {
          document.removeEventListener(event, handlers[index]);
        }
      });
    };
  }, []);

  useEffect(() => {
    if (!supportsNotifications()) return;
    const checkPermission = () => {
      setPermission(globalThis.Notification.permission);
    };
    checkPermission();
    window.addEventListener('focus', checkPermission);
    document.addEventListener('visibilitychange', checkPermission);
    return () => {
      window.removeEventListener('focus', checkPermission);
      document.removeEventListener('visibilitychange', checkPermission);
    };
  }, []);

  const requestPermission = useCallback(async (): Promise<NotificationPermission> => {
    if (!supportsNotifications()) return 'denied';
    if (permission === 'granted') return 'granted';
    try {
      const result = await globalThis.Notification.requestPermission();
      setPermission(result);
      return result;
    } catch (error) {
      console.error('Failed to request notification permission:', error);
      return 'denied';
    }
  }, [permission]);

  const updateSettings = useCallback((newSettings: Partial<NotificationSettings>) => {
    setSettings((prev) => ({ ...prev, ...newSettings }));
    saveNotificationSettings(newSettings);
  }, []);

  const showNotification = useCallback(
    (
      message: Message,
      conversation: Conversation,
      opts?: { skipSound?: boolean; skipNotification?: boolean }
    ) => {
      if (!currentUserId) return;
      if (conversation.isMuted?.[currentUserId]) return;
      if (!settings.enabled) return;
      if (message.senderId === currentUserId) return;
      const conversationNotifications = previousMessagesRef.current.get(conversation.id) ?? new Set<string>();
      if (conversationNotifications.has(message.id)) return;
      conversationNotifications.add(message.id);
      previousMessagesRef.current.set(conversation.id, conversationNotifications);
      if (conversationNotifications.size > 50) {
        const idsArray = Array.from(conversationNotifications);
        conversationNotifications.clear();
        idsArray.slice(-50).forEach((id) => conversationNotifications.add(id));
      }
      persistNotificationTracking();
      if (!opts?.skipSound && settings.soundEnabled && supportsAudio()) {
        playNotificationSound(settings.soundVolume).catch((err) => {
          if (err instanceof Error && err.name !== 'NotAllowedError') {
            console.warn('Sound playback failed:', err);
          }
        });
      }
      if (!opts?.skipNotification && permission === 'granted') {
        try {
          if (globalThis.Notification.permission !== 'granted') return;
          const otherParticipant = Object.values(conversation.participantDetails).find(
            (p: ParticipantInfo) => p?.name
          );
          const conversationName =
            conversation.type === 'group'
              ? conversation.name ?? 'Group Chat'
              : (otherParticipant?.name as string | undefined) ?? 'Chat';
          const notificationOptions: NotificationOptions = {
            body: message.content.substring(0, 100),
            tag: conversation.id,
            requireInteraction: false,
            silent: !settings.soundEnabled,
          };
          try {
            notificationOptions.icon = (conversation as { avatar?: string }).avatar ?? '/favicon.ico';
            notificationOptions.badge = '/favicon.ico';
          } catch {
            /**/
          }
          const notification = new globalThis.Notification(conversationName, notificationOptions);
          notification.onclick = () => {
            window.focus();
            navigate(chatPath);
            setTimeout(() => {
              window.dispatchEvent(
                new globalThis.CustomEvent('selectConversation', { detail: { conversationId: conversation.id } })
              );
            }, 100);
            notification.close();
          };
          setTimeout(() => notification.close(), 5000);
        } catch (error) {
          console.error('Failed to show notification:', error);
        }
      }
    },
    [currentUserId, settings, permission, navigate, chatPath, persistNotificationTracking]
  );

  const checkForNewMessages = useCallback(
    (conversationId: string, newMessages: Message[], conversation: Conversation) => {
      if (!currentUserId || !settings.enabled) return;
      const previousMessageIds = previousMessagesRef.current.get(conversationId) ?? new Set<string>();
      newMessages.forEach((message) => {
        if (message.type === 'system' || message.senderId === currentUserId) return;
        if (!previousMessageIds.has(message.id)) {
          showNotification(message, conversation);
        }
      });
      const newMessageIds = new Set(newMessages.map((m) => m.id));
      previousMessagesRef.current.set(conversationId, newMessageIds);
      persistNotificationTracking();
    },
    [currentUserId, settings.enabled, showNotification, persistNotificationTracking]
  );

  useEffect(() => {
    const cleanup = setInterval(() => {
      if (previousMessagesRef.current.size > 10) {
        const entries = Array.from(previousMessagesRef.current.entries());
        previousMessagesRef.current.clear();
        entries.slice(-10).forEach(([id, messages]) => {
          previousMessagesRef.current.set(id, messages);
        });
      }
    }, 60000);
    return () => clearInterval(cleanup);
  }, []);

  return {
    permission,
    settings,
    supportsNotifications: supportsNotifications(),
    requestPermission,
    updateSettings,
    showNotification,
    checkForNewMessages,
  };
};
